import { useEffect, useMemo, useState } from 'react'
import { Spin, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { getPortfolioPositions } from '@/api/portfolio'
import type { PortfolioPosition } from '@/types/portfolio'
import { filterFormatWanYiNumber } from '@/utils/filters'

/** market_value 为原始币种金额（元），按万/亿展示 */
function fmtMv(v: number | null | undefined) {
    if (v == null) return '--'
    return filterFormatWanYiNumber(v)
}

type HoldingRow = PortfolioPosition & { display_weight: number }

function buildColumns(): ColumnsType<HoldingRow> {
    return [
        {
            title: '股票', key: 'stock',
            render: (_: unknown, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>
                        {r.stock_name || r.stock_code || '--'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.stock_code}</div>
                </div>
            ),
        },
        {
            title: '持仓数量', dataIndex: 'shares', key: 'shares',
            render: (v: number, r) => {
                const sellable = r.is_short
                    ? '空头'
                    : `可卖 ${(r.sellable_shares ?? 0).toLocaleString()}`
                const frozen = `冻结 ${(r.frozen_shares ?? 0).toLocaleString()}`
                const pending = `在途 ${(r.pending_shares ?? 0).toLocaleString()}`
                return (
                    <div>
                        <div>{Number(v || 0).toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                            {[sellable, frozen, pending].join(' · ')}
                        </div>
                    </div>
                )
            },
        },
        {
            title: '今日涨跌幅', key: 'today_pnl_pct',
            render: (_: unknown, r) => {
                const pct = r.today_pnl_pct
                if (pct == null || !Number.isFinite(pct)) {
                    return <span style={{ color: 'var(--text-3)' }}>--</span>
                }
                const tone = pct >= 0 ? '#ef4444' : '#22c55e'
                return (
                    <span style={{ color: tone, fontWeight: 700 }}>
                        {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                    </span>
                )
            },
        },
        {
            title: '现价 / 成本', key: 'price',
            render: (_: unknown, r) => (
                <div>
                    <div style={{ color: '#fff', fontWeight: 600 }}>
                        {r.current_price != null ? r.current_price.toFixed(2) : '--'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        成本 {(r.cost_price ?? 0).toFixed(2)}
                    </div>
                </div>
            ),
        },
        {
            title: '浮盈亏', key: 'pnl',
            render: (_: unknown, r) => {
                const pct = r.unrealized_pnl_pct
                const amt = r.unrealized_pnl
                if (pct == null && amt == null) {
                    return <span style={{ color: 'var(--text-3)' }}>--</span>
                }
                const tone = (pct ?? amt ?? 0) >= 0 ? '#ef4444' : '#22c55e'
                return (
                    <div>
                        {pct != null ? (
                            <div style={{ color: tone, fontWeight: 700 }}>
                                {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                            </div>
                        ) : null}
                        {amt != null ? (
                            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                                {amt >= 0 ? '+' : ''}{fmtMv(amt)}
                            </div>
                        ) : null}
                    </div>
                )
            },
        },
        {
            title: '市值', dataIndex: 'market_value', key: 'mv',
            render: (v: number | null) => fmtMv(v),
        },
        {
            title: '权重', dataIndex: 'display_weight', key: 'weight',
            render: (v: number) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 6, background: 'rgba(148,163,184,0.15)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, Math.max(0, v))}%`, height: '100%', background: 'var(--accent)', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-2)', minWidth: 42 }}>{v.toFixed(1)}%</span>
                </div>
            ),
        },
    ]
}

export default function Holdings({
    portfolioId,
    totalAssets,
}: {
    portfolioId: number
    /** 组合总资产（元），用于临时计算权重 = 市值 / 总资产 */
    totalAssets?: number
}) {
    const [items, setItems] = useState<PortfolioPosition[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        getPortfolioPositions(portfolioId)
            .then(res => setItems(res.items ?? []))
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [portfolioId])

    const rows = useMemo<HoldingRow[]>(() => {
        const mvSum = items.reduce((sum, item) => sum + (Number(item.market_value) || 0), 0)
        const base = (totalAssets && totalAssets > 0) ? totalAssets : mvSum
        return items.map(item => {
            const mv = Number(item.market_value) || 0
            const display_weight = base > 0 ? (mv / base) * 100 : 0
            return { ...item, display_weight }
        })
    }, [items, totalAssets])

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin /></div>
    }

    if (rows.length === 0) {
        return <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-3)' }}>暂无持仓数据</div>
    }

    return (
        <Table
            dataSource={rows}
            columns={buildColumns()}
            rowKey="id"
            pagination={false}
            size="small"
        />
    )
}
